import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Sidebar from "../../GlobalManagement/Pages/Sidebar";
import BackButton from "../../GlobalManagement/Pages/BackButton";
import MobileHeaderToggle from "../../GlobalManagement/Pages/MobileHeaderToggle";
import SearchBar from "../../GlobalManagement/Pages/SearchBar";
import { Key, RefreshCw, Save, Edit3, XCircle, Shield } from "lucide-react";
import toast from "react-hot-toast";
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterAccentButton, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell, 
  masterInput, 
  masterSelect, 
  masterTableCellstatus,
  masterTableDatastatus,
  masterInputLabel
} from '../../GlobalManagement/Pages/masterPageStyles';

const normalizeEmployeeId = (value) => String(value || "").trim().toUpperCase();
const isExEmployeeId = (value) => normalizeEmployeeId(value).startsWith("EX-");
const getEmployeePrefix = (value) => {
  const normalized = normalizeEmployeeId(value);
  if (!normalized || isExEmployeeId(normalized)) return "";
  return normalized.split("-")[0] || "";
};

const EmployeeUserIdCreated = () => {
  const [dataList, setDataList] = useState([]);
  const [bulkPassword, setBulkPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [tempPassword, setTempPassword] = useState("");
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployeePrefix, setSelectedEmployeePrefix] = useState("ALL");

  // Pagination states matching Master standard
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(18);

  const token = localStorage.getItem("token");
  const API_URL = "/api/employee-ids";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDataList((res.data || []).filter((emp) => !isExEmployeeId(emp.employeeId)));
    } catch (err) {
      toast.error("Error loading employees");
    }
  };

  const togglePasswordVisibility = (id) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const normalizeSearchInput = (value) => {
    let v = String(value || "").toUpperCase();
    if (/^[A-Z]+\d/.test(v)) {
      v = v.replace(/^([A-Z]+)-?(\d.*)$/, "$1-$2");
    }
    return v;
  };

  const filteredDataList = useMemo(() => {
    return dataList.filter((emp) => {
      if (isExEmployeeId(emp.employeeId)) return false;
      const prefixMatch =
        selectedEmployeePrefix === "ALL" ||
        getEmployeePrefix(emp.employeeId) === selectedEmployeePrefix;
      const q = String(searchTerm || "").toUpperCase().trim();
      if (!prefixMatch) return false;
      if (!q) return true;
      return (
        String(emp.name || "").toUpperCase().includes(q) ||
        String(emp.employeeUserId || "").toUpperCase().includes(q) ||
        String(emp.employeeId || "").toUpperCase().includes(q)
      );
    });
  }, [dataList, selectedEmployeePrefix, searchTerm]);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredDataList;
    const start = (currentPage - 1) * pageSize;
    return filteredDataList.slice(start, start + pageSize);
  }, [filteredDataList, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedEmployeePrefix, pageSize]);

  const employeePrefixOptions = ["ALL", ...new Set(
    dataList.map((emp) => getEmployeePrefix(emp.employeeId)).filter(Boolean)
  )];

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredDataList.map((emp) => emp.employeeId));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkGenerate = async () => {
    if (!bulkPassword) return toast.error("Please enter a password first!");
    const targetEmployeeIds =
      selectedIds.length > 0 ? selectedIds : filteredDataList.map((emp) => emp.employeeId);
    if (targetEmployeeIds.length === 0) {
      toast.error("No active employees found for password generation");
      return;
    }
    const message = selectedIds.length > 0 
      ? `Update password for ${selectedIds.length} selected employees?`
      : `Update password for ${targetEmployeeIds.length} filtered employees?`;

    if (!window.confirm(message)) return;

    setLoading(true);
    try {
      await axios.post(`${API_URL}/generate-all-passwords`, 
        { 
          customPassword: bulkPassword,
          targetEmployeeIds
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Passwords generated successfully!");
      setBulkPassword("");
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (emp) => {
    setEditingRow(emp.employeeId);
    setTempPassword(""); 
  };

  const saveIndividualPassword = async (emp) => {
    if (!tempPassword) return toast.error("Password cannot be empty");
    try {
      await axios.post(`${API_URL}/generate-all-passwords`, 
        { 
          customPassword: tempPassword,
          targetEmployeeIds: [emp.employeeId] 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Password updated for ${emp.name}`);
      setEditingRow(null);
      fetchData();
    } catch (err) {
      toast.error("Failed to update individual password");
    }
  };

  return (
    <div className={masterBodyArea}>
      <Sidebar />
      <div className={`${masterPageShell} ${masterContentArea}`}>
        
        {/* Header Section */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><Shield size={14} /></div>
              <div><h1 className={masterTitle}>Employee Password Management</h1></div>
            </div>
            <BackButton />
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full overflow-y-auto`}>
          <div className="p-2 space-y-4">
            
            {/* Top Bulk Action Section Panel */}
            <div className={masterPanel + " p-4"}>
              <MobileHeaderToggle>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-end">
                  <div className="flex flex-col">
                    <label className={masterInputLabel + " mb-1"}>
                      {selectedIds.length > 0 
                        ? `Target: (${selectedIds.length}) Selected` 
                        : "Enter Password"}
                    </label>
                    <input
                      type="text"
                      placeholder="Enter password to generate..."
                      value={bulkPassword}
                      onChange={(e) => setBulkPassword(e.target.value)}
                      className={masterInput}
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className={masterInputLabel + " mb-1"}>Status</label>
                    <select
                      value={selectedEmployeePrefix}
                      onChange={(e) => setSelectedEmployeePrefix(e.target.value)}
                      className={masterSelect}
                    >
                      {employeePrefixOptions.map((prefix) => (
                        <option key={prefix} value={prefix}>
                          {prefix}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col">
                    <label className={masterInputLabel + " mb-1"}>Search Employee</label>
                    <input
                      type="text"
                      placeholder="Search by Name / User ID / Employee ID"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(normalizeSearchInput(e.target.value))}
                      className={masterInput + " uppercase"}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleBulkGenerate}
                    disabled={loading}
                    className={masterAccentButton + " h-[34px] flex items-center justify-center gap-2"}
                  >
                    {loading ? <RefreshCw className="animate-spin" size={14} /> : <Key size={14} />}
                    <span className="whitespace-nowrap">
                      {selectedIds.length > 0 ? "Generate Selected" : "Generate All"}
                    </span>
                  </button>
                </div>
              </MobileHeaderToggle>
            </div>

            {/* Table Section Panel with integrated SearchBar & Pagination */}
            <div className={`${masterPanel} flex flex-col overflow-hidden w-full`}>
              <div className="border-b border-slate-200 flex-shrink-0 bg-white p-2">
                <SearchBar 
                  value={searchTerm} 
                  onChange={(val) => {
                    setSearchTerm(normalizeSearchInput(val));
                    setCurrentPage(1);
                  }} 
                  placeholder="Search by name, user ID, or employee ID..." 
                  totalItems={filteredDataList.length} 
                  currentPage={currentPage} 
                  pageSize={pageSize} 
                  onPageChange={setCurrentPage} 
                  onPageSizeChange={setPageSize} 
                />
              </div>

              <div className="overflow-x-auto w-full">
                <table className={masterTable}>
                  <thead>
                    <tr className={masterStickyHeadRow}>
                      <th className={masterTableData} style={{ width: '40px' }}>
                        <input 
                          type="checkbox" 
                          onChange={handleSelectAll}
                          checked={
                            filteredDataList.length > 0 &&
                            filteredDataList.every((emp) => selectedIds.includes(emp.employeeId))
                          }
                          className="cursor-pointer"
                        />
                      </th>
                      <th className={masterTableData} style={{ width: '60px' }}>Sl No</th>
                      <th className={masterTableData}>Employee ID</th>
                      <th className={masterTableData}>User ID</th>
                      <th className={masterTableData}>Name</th>
                      <th className={masterTableData}>Designation</th>
                      <th className={masterTableData}>Password Status</th>
                      <th className={masterTableDatastatus}>Action</th>
                    </tr>
                  </thead>
                  <tbody className={masterTbody}>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((emp, index) => {
                        const globalIndex = pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1;
                        const isSelected = selectedIds.includes(emp.employeeId);
                        return (
                          <tr 
                            key={index} 
                            className={`${isSelected ? 'bg-orange-50' : masterTableRow} transition-colors`}
                          >
                            <td className={masterTableCell}>
                              <input 
                                type="checkbox" 
                                checked={isSelected}
                                onChange={() => handleSelectOne(emp.employeeId)}
                                className="cursor-pointer"
                              />
                            </td>
                            <td className={masterTableCell}>{globalIndex}</td>
                            <td className={`${masterTableCell} font-semibold text-slate-800`}>{emp.employeeId}</td>
                            <td className={`${masterTableCell} font-bold text-blue-900`}>{emp.employeeUserId}</td>
                            <td className={`${masterTableCell} font-medium uppercase`}>{emp.name}</td>
                            <td className={`${masterTableCell} text-xs text-slate-600 truncate max-w-[150px]`} title={emp.designation}>
                              {emp.designation}
                            </td>
                            <td className={masterTableCell}>
                              {editingRow === emp.employeeId ? (
                                <input 
                                  type="text"
                                  placeholder="Set Password"
                                  value={tempPassword}
                                  onChange={(e) => setTempPassword(e.target.value)}
                                  className={masterInput + " max-w-[120px] text-xs"}
                                  autoFocus
                                />
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-0.5 rounded font-bold text-[10px] ${
                                    !emp.hasPassword
                                    ? "bg-red-50 text-red-500 border border-red-200" 
                                    : "bg-green-50 text-green-600 border border-green-200"
                                  }`}>
                                    {!emp.hasPassword
                                      ? "NONE" 
                                      : (visiblePasswords[emp.employeeId] ? emp.plainPassword : "GENERATED")}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className={masterTableCellstatus}>
                              {editingRow === emp.employeeId ? (
                                <div className="flex justify-center gap-2">
                                  <button type="button" onClick={() => saveIndividualPassword(emp)} className="text-green-600 hover:text-green-800 p-1" title="Save">
                                    <Save size={14} />
                                  </button>
                                  <button type="button" onClick={() => setEditingRow(null)} className="text-red-500 hover:text-red-700 p-1" title="Cancel">
                                    <XCircle size={14} />
                                  </button>
                                </div>
                              ) : (
                                <button type="button" onClick={() => startEdit(emp)} className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-all" title="Edit Password">
                                  <Edit3 size={14} />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">
                          No employees found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};

export default EmployeeUserIdCreated;